import { useLocation } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useMediaPlayer } from "../lib/mediaPlayer";
import { share, trackShareUrl } from "../lib/share";
import { Play, Pause, SkipForward, SkipBack, Shuffle, Volume2, VolumeX, X, Share2 } from "lucide-react";

// Only permanently-hidden route (admin doesn't need a music widget)
const HIDE_ROUTES = new Set(["/admin"]);

// Small marquee that auto-scrolls only when the text overflows its wrapper.
// On overflow, renders the string twice so the loop is seamless; pauses on hover.
function Marquee({ text, className = "" }) {
  const wrapRef = useRef(null);
  const contentRef = useRef(null);
  const [overflow, setOverflow] = useState(false);

  useEffect(() => {
    const check = () => {
      const wrap = wrapRef.current;
      const content = contentRef.current;
      if (!wrap || !content) return;
      // Compare the full content width against the visible wrapper width.
      // Add a 2px tolerance so a sub-pixel diff doesn't false-trigger.
      setOverflow(content.scrollWidth > wrap.clientWidth + 2);
    };
    check();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(check) : null;
    if (ro && wrapRef.current) ro.observe(wrapRef.current);
    if (ro && contentRef.current) ro.observe(contentRef.current);
    return () => { if (ro) ro.disconnect(); };
  }, [text]);

  return (
    <div ref={wrapRef} className={`overflow-hidden whitespace-nowrap group/marquee ${className}`}>
      <span
        ref={contentRef}
        className={`inline-block ${overflow ? "mini-marquee-anim" : ""}`}
        style={overflow ? undefined : { maxWidth: "100%" }}
      >
        {text}
        {overflow && <span aria-hidden="true" className="inline-block pl-8">{text}</span>}
      </span>
    </div>
  );
}

export default function MiniPlayer() {
  const p = useMediaPlayer();
  const loc = useLocation();
  const [volumeOpen, setVolumeOpen] = useState(false);
  const volumeRef = useRef(null);

  // Close the volume popover when the user clicks outside of it. This replaces
  // the previous 3-second auto-close timer that was too aggressive.
  useEffect(() => {
    if (!volumeOpen) return;
    const onDown = (e) => {
      if (!volumeRef.current) return;
      if (!volumeRef.current.contains(e.target)) setVolumeOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [volumeOpen]);

  if (!p.showMini) return null;
  if (!p.currentTrack) return null;
  if (HIDE_ROUTES.has(loc.pathname)) return null;
  if (p.fullPlayerVisible) return null; // on-page FamRadioPlayer is visible — no need for mini

  const t = p.currentTrack;
  const pct = p.progress.duration > 0 ? (p.progress.time / p.progress.duration) * 100 : 0;

  const shareTrack = () => {
    share({
      title: t.title || "Untitled",
      text: `${t.title || "Untitled"}${t.artist ? ` — ${t.artist}` : ""} · SyndaFam`,
      url: trackShareUrl(t.id),
    });
  };

  const volumePct = p.muted ? 0 : Math.round(p.volume * 100);

  return (
    <div
      className="fixed z-[80] glass-card-strong rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.6),0_0_40px_rgba(230,57,70,0.15)] left-3 right-3 bottom-3 sm:left-auto sm:right-4 sm:bottom-4 sm:w-[380px] lg:w-[420px] sm:max-w-[calc(100vw-2rem)]"
      data-testid="mini-player"
    >
      {/* Local marquee keyframes — kept in-component so the file is fully self-contained. */}
      <style>{`
        @keyframes mini-marquee {
          0%   { transform: translateX(0); }
          12%  { transform: translateX(0); }
          92%  { transform: translateX(-50%); }
          100% { transform: translateX(-50%); }
        }
        .mini-marquee-anim { animation: mini-marquee 14s linear infinite; will-change: transform; }
        .group\\/marquee:hover .mini-marquee-anim { animation-play-state: paused; }

        /* Sleek minimalist vertical volume slider — subtle glass track with a
           small round amber thumb. Works in Chromium (::-webkit-slider-*)
           and Firefox (::-moz-range-*). */
        .mini-volume-vertical { background: transparent; }
        .mini-volume-vertical::-webkit-slider-runnable-track {
          width: 4px;
          border-radius: 999px;
          background: rgba(255,255,255,0.12);
        }
        .mini-volume-vertical::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 999px;
          background: #FCA311;
          border: 2px solid rgba(0,0,0,0.6);
          box-shadow: 0 0 0 1px rgba(252,163,17,0.5), 0 4px 10px rgba(0,0,0,0.5);
          margin-left: -5px; /* re-centre thumb on vertical track */
          cursor: pointer;
          transition: transform 120ms ease;
        }
        .mini-volume-vertical:hover::-webkit-slider-thumb { transform: scale(1.15); }
        .mini-volume-vertical::-moz-range-track {
          width: 4px;
          border-radius: 999px;
          background: rgba(255,255,255,0.12);
        }
        .mini-volume-vertical::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 999px;
          background: #FCA311;
          border: 2px solid rgba(0,0,0,0.6);
          box-shadow: 0 0 0 1px rgba(252,163,17,0.5), 0 4px 10px rgba(0,0,0,0.5);
          cursor: pointer;
        }
      `}</style>

      {/* soft breathing glow strips */}
      <div className="absolute -top-8 -left-8 w-32 h-32 rounded-full bg-[#E63946]/25 blur-2xl pointer-events-none breathe" />
      <div className="absolute -bottom-10 -right-10 w-32 h-32 rounded-full bg-[#FCA311]/20 blur-2xl pointer-events-none breathe" style={{ animationDelay: "-1.8s" }} />

      <div className="relative flex items-center gap-2 p-2.5 sm:p-3">
        {/* Play / Pause */}
        <div className="relative flex-shrink-0">
          {p.playing && (
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full bg-[#E63946]/50 blur-lg breathe pointer-events-none"
            />
          )}
          <button
            onClick={p.togglePlay}
            className="relative w-10 h-10 rounded-full bg-[#E63946] hover:bg-[#C92A36] flex items-center justify-center shadow-[0_0_24px_rgba(230,57,70,0.55)] transition-transform hover:scale-105"
            aria-label={p.playing ? "Pause" : "Play"}
            data-testid="mini-play-toggle"
          >
            {p.playing ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white ml-0.5" />}
          </button>
        </div>

        {/* Track meta — marquee scroll on overflow */}
        <div className="flex-1 min-w-0">
          <Marquee
            text={`FAM RADIO · ${(t.media_type || "audio").toUpperCase()}`}
            className="font-mono text-[10px] tracking-widest text-[#FCA311]"
          />
          <Marquee
            text={t.title || "Untitled"}
            className="font-display text-sm leading-tight"
          />
          {t.artist && (
            <Marquee
              text={t.artist}
              className="font-mono text-[10px] text-white/50"
            />
          )}
        </div>

        {/* Controls cluster — some controls hide on very small screens to keep the widget readable */}
        <div className="flex items-center gap-0 flex-shrink-0">
          {/* Shuffle — hidden on <sm to save space; shuffle stays accessible via the on-page player */}
          <button
            onClick={() => p.setShuffle(!p.shuffle)}
            className={`hidden sm:flex w-8 h-8 rounded-full items-center justify-center transition-colors ${p.shuffle ? "text-[#FCA311] bg-white/5" : "text-white/60 hover:text-white hover:bg-white/5"}`}
            aria-label={p.shuffle ? "Shuffle on" : "Shuffle off"}
            title={p.shuffle ? "Shuffle: ON" : "Shuffle: OFF"}
            data-testid="mini-shuffle"
          >
            <Shuffle className="w-3.5 h-3.5" />
          </button>

          {/* Back / Prev */}
          <button
            onClick={p.prevTrack}
            className="w-8 h-8 rounded-full hover:bg-white/5 flex items-center justify-center text-white/80"
            aria-label="Previous track"
            title="Previous"
            data-testid="mini-prev"
          >
            <SkipBack className="w-4 h-4" />
          </button>

          {/* Skip / Next */}
          <button
            onClick={p.nextTrack}
            className="w-8 h-8 rounded-full hover:bg-white/5 flex items-center justify-center text-white/80"
            aria-label="Next track"
            title="Next"
            data-testid="mini-next"
          >
            <SkipForward className="w-4 h-4" />
          </button>

          {/* Share — hidden on <sm */}
          <button
            onClick={shareTrack}
            className="hidden sm:flex w-8 h-8 rounded-full hover:bg-white/5 items-center justify-center text-white/80 hover:text-[#FCA311] transition-colors"
            aria-label={`Copy share link for ${t.title || "current track"}`}
            title="Copy share link"
            data-testid="mini-share"
          >
            <Share2 className="w-3.5 h-3.5" />
          </button>

          {/* Volume — hover-reveal vertical slider that floats above the icon.
              Uses a shared `group` hover state on the wrapper so moving the
              cursor into the popover keeps it open. Click still toggles for
              keyboard / touch users (touch devices don't have hover). */}
          <div
            className="relative group/volume"
            ref={volumeRef}
            onMouseEnter={() => setVolumeOpen(true)}
            onMouseLeave={() => setVolumeOpen(false)}
          >
            <button
              onClick={() => setVolumeOpen((o) => !o)}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${volumeOpen ? "text-[#FCA311] bg-white/5" : "text-white/80 hover:bg-white/5"}`}
              aria-label="Volume controls"
              aria-expanded={volumeOpen}
              title={`Volume: ${p.muted ? "muted" : volumePct + "%"}`}
              data-testid="mini-volume-toggle"
            >
              {p.muted || p.volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            {/* Invisible hover bridge — 8px gap-filler so cursor can travel
                from the button to the slider without triggering onMouseLeave. */}
            <div className={`pointer-events-none absolute bottom-full right-0 w-full h-2 ${volumeOpen ? "" : "hidden"}`} />
            <div
              className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 flex flex-col items-center gap-2 py-3 px-2 rounded-2xl backdrop-blur-xl bg-black/40 border border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.55)] transition-all duration-200 ${volumeOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-1 pointer-events-none"}`}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-hidden={!volumeOpen}
              data-testid="mini-volume-popover"
            >
              <div className="font-mono text-[10px] tabular-nums text-white/80 select-none">
                {p.muted ? "OFF" : volumePct}
              </div>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={volumePct}
                orient="vertical"
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10) / 100;
                  p.setVolume(v);
                  if (v > 0 && p.muted) p.setMuted(false);
                  if (v === 0 && !p.muted) p.setMuted(true);
                }}
                style={{
                  writingMode: "vertical-lr",
                  direction: "rtl",
                  WebkitAppearance: "slider-vertical",
                  appearance: "slider-vertical",
                  width: 6,
                  height: 120,
                }}
                className="mini-volume-vertical accent-[#FCA311] cursor-pointer"
                aria-label="Volume"
                data-testid="mini-volume-slider"
              />
              <button
                onClick={() => p.setMuted(!p.muted)}
                className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${p.muted || p.volume === 0 ? "text-[#E63946]" : "text-white/60 hover:text-[#FCA311]"}`}
                aria-label={p.muted ? "Unmute" : "Mute"}
                title={p.muted ? "Unmute" : "Mute"}
                data-testid="mini-volume-mute"
              >
                {p.muted || p.volume === 0 ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>

          {/* Hide */}
          <button
            onClick={() => p.setShowMini(false)}
            className="w-7 h-7 hover:text-[#E63946] flex items-center justify-center text-white/40"
            aria-label="Hide mini player"
            title="Hide"
            data-testid="mini-hide"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative h-1 bg-white/5 overflow-hidden rounded-b-2xl">
        <div className="h-full bg-gradient-to-r from-[#E63946] to-[#FCA311] transition-all shadow-[0_0_12px_rgba(230,57,70,0.7)]" style={{ width: `${pct}%` }} data-testid="mini-progress-bar" />
      </div>
    </div>
  );
}
